import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getProducts, getProductById, getFarmById, getFarmReviewSummary } from "../../Utils/storeService";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { useToast } from "../../context/ToastContext";
import { formatLek } from "../../Utils/currency";
import { useRecentlyViewed } from "../../hooks/useRecentlyViewed";
import { useShare } from "../../hooks/useShare";
import RatingStars from "../../ui/RatingStars";
import "./ProductPage.css";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addItemToCart } = useCart();
  const { peekDrawer } = useCartDrawer();
  const { success: toastSuccess } = useToast();
  const { addViewed } = useRecentlyViewed();
  const share = useShare();

  const [product, setProduct] = useState(null);
  const [farm, setFarm] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        // Try single-product endpoint first, fall back to full list
        let found;
        let allProducts;
        try {
          found = await getProductById(id);
        } catch {
          allProducts = await getProducts();
          found = (allProducts || []).find((p) => String(p.id) === String(id));
        }
        if (!found) {
          if (active) setLoading(false);
          return;
        }

        const mapped = {
          id: found.id,
          farmId: found.farmId,
          name: found.name,
          description: found.description || "",
          stock: Number(found.stock || 0),
          price: Number(found.price || 0),
          unit: found.unit || "",
          image:
            found.imageUrl ||
            `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" fill="%231a1a2e"><rect width="600" height="400"/><text x="300" y="205" fill="%23666" font-size="18" text-anchor="middle">${found.name || "Product"}</text></svg>`)}`,
          badges: [],
        };
        if (/organic/i.test(mapped.description)) mapped.badges.push("Organic");
        if (/pesticide/i.test(mapped.description)) mapped.badges.push("Pesticide-Free");

        if (active) {
          setProduct(mapped);
          addViewed(mapped);
        }

        // Load farm info
        if (found.farmId) {
          try {
            const farmData = await getFarmById(found.farmId);
            if (active) setFarm(farmData);
          } catch { /* ignore */ }
          // Interim social proof: surface the farm's rating until per-product
          // reviews exist in the API.
          try {
            const summary = await getFarmReviewSummary(found.farmId);
            if (active && summary && summary.totalReviews > 0) setReviewSummary(summary);
          } catch { /* ignore */ }
        }

        // Related products from same farm
        if (!allProducts && found.farmId) {
          try { allProducts = await getProducts(found.farmId); } catch { allProducts = []; }
        }
        const related = (allProducts || [])
          .filter((p) => p.farmId === found.farmId && String(p.id) !== String(id))
          .slice(0, 4)
          .map((p) => ({
            id: p.id,
            name: p.name,
            price: Number(p.price || 0),
            image:
              p.imageUrl ||
              `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" fill="%231a1a2e"><rect width="300" height="200"/><text x="150" y="105" fill="%23666" font-size="14" text-anchor="middle">${p.name || "Product"}</text></svg>`)}`,
          }));
        if (active) setRelatedProducts(related);
      } catch (err) {
        console.error("Failed to load product", err);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [id, addViewed]);

  const handleAddToCart = () => {
    if (!product) return;
    // Pass farmId/farmName so the cart can group this line by farm. The add can
    // still be refused on stock, in which case the context has already shown the
    // reason and returned false, so we must not also show a success toast.
    const added = addItemToCart({
      id: product.id,
      farmId: product.farmId,
      farmName: farm?.name || "",
      name: product.name,
      price: product.price,
      image: product.image,
      unit: product.unit,
      description: product.description,
      quantity,
    });
    if (added) {
      peekDrawer();
      toastSuccess(
        `${product.name} ${t("cart.added", { defaultValue: "added to cart" })}`
      );
    }
  };

  if (loading) {
    return (
      <div className="product-page">
        <div className="product-page-container">
          <div className="pp-skeleton-image" />
          <div className="pp-skeleton-info">
            <div className="pp-skeleton-line" style={{ width: "60%", height: 28 }} />
            <div className="pp-skeleton-line" style={{ width: "30%", height: 20 }} />
            <div className="pp-skeleton-line" style={{ width: "100%", height: 60 }} />
            <div className="pp-skeleton-line" style={{ width: "40%", height: 44 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-page">
        <div className="product-page-container pp-not-found">
          <h2>{t("products.not_found", { defaultValue: "Product not found" })}</h2>
          <p>{t("products.not_found_desc", { defaultValue: "This product may have been removed or doesn't exist." })}</p>
          <button className="pp-back-btn" onClick={() => navigate("/products")}>
            {t("products.browse_all", { defaultValue: "Browse All Products" })}
          </button>
        </div>
      </div>
    );
  }

  const stockLabel =
    product.stock > 10
      ? t("products.in_stock", { defaultValue: "In Stock" })
      : product.stock > 0
      ? t("products.low_stock", { defaultValue: "Only {{count}} left", count: product.stock })
      : t("products.out_of_stock", { defaultValue: "Out of Stock" });

  return (
    <div className="product-page">
      <div className="product-page-container">
        <div className="pp-breadcrumb">
          <Link to="/products">{t("nav.products", { defaultValue: "Products" })}</Link>
          <span> / </span>
          <span>{product.name}</span>
        </div>

        <div className="pp-main">
          <div className="pp-image-section">
            <img
              src={product.image}
              alt={product.name}
              className="pp-image"
              loading="lazy"
            />
          </div>

          <div className="pp-info-section">
            <h1 className="pp-title">{product.name}</h1>

            <div className="pp-price">
              {formatLek(product.price)}
              {product.unit ? <span className="pp-unit"> / {product.unit}</span> : null}
            </div>

            <div className={`pp-stock ${product.stock === 0 ? "pp-out" : product.stock <= 10 ? "pp-low" : ""}`}>
              {stockLabel}
            </div>

            {reviewSummary && farm && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  margin: "2px 0 6px",
                }}
              >
                <RatingStars
                  rating={reviewSummary.averageRating}
                  count={reviewSummary.totalReviews}
                />
                <Link
                  to={`/farms/${farm.id}`}
                  style={{ color: "var(--color-text-secondary)", fontSize: "0.8rem" }}
                >
                  {t("products.reviews_for", {
                    defaultValue: "Reviews for {{farm}}",
                    farm: farm.name,
                  })}
                </Link>
              </div>
            )}

            {product.badges.length > 0 && (
              <div className="pp-badges">
                {product.badges.map((b) => (
                  <span key={b} className="pp-badge">{b}</span>
                ))}
              </div>
            )}

            <p className="pp-description">{product.description}</p>

            {farm && (
              <div className="pp-farm-info">
                <Link to={`/farms/${farm.id}`} className="pp-farm-link">
                  {farm.logoImageUrl && (
                    <img src={farm.logoImageUrl} alt={farm.name} className="pp-farm-logo" loading="lazy" />
                  )}
                  <span>{farm.name}</span>
                </Link>
                {farm.location && <span className="pp-farm-location">{farm.location}</span>}
              </div>
            )}

            <div className="pp-actions">
              <div className="pp-quantity">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  –
                </button>
                <span>{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  disabled={product.stock > 0 && quantity >= product.stock}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button
                className="pp-add-to-cart"
                onClick={handleAddToCart}
                disabled={product.stock === 0}
              >
                {product.stock === 0
                  ? t("products.out_of_stock", { defaultValue: "Out of Stock" })
                  : t("products.add_to_cart", { defaultValue: "Add to Cart" })}
              </button>
            </div>

            <div className="pp-bottom-row">
              <p className="pp-traceability">
                {t("products.traceability", { defaultValue: "Traceability" })}: PRD-{product.id}
              </p>
              <button
                className="pp-share-btn"
                type="button"
                onClick={() => share({
                  title: product.name,
                  text: t("products.share_text", {
                    defaultValue: "Check out {{name}} on Dora e Fshatit!",
                    name: product.name,
                  }),
                  url: window.location.href,
                })}
              >
                {t("products.share", { defaultValue: "Share" })}
              </button>
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="pp-related">
            <h2 className="pp-related-title">
              {t("products.from_same_farm", { defaultValue: "More from this Farm" })}
            </h2>
            <div className="pp-related-grid">
              {relatedProducts.map((rp) => (
                <div
                  key={rp.id}
                  className="pp-related-card"
                  onClick={() => navigate(`/products/${rp.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate(`/products/${rp.id}`); }}
                >
                  <img src={rp.image} alt={rp.name} loading="lazy" />
                  <h3>{rp.name}</h3>
                  <p>{formatLek(rp.price)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
